import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock3, ListTodo, Target } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { GoalsCard } from '@/components/goals';
import { goalService } from '@/lib/api/goal.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { useTeamMemberRole } from '@/hooks/useTeamMemberRole';
import { canEditCelebration } from '@/utils/permissions';
import type { Celebration } from '@/types/celebration.types';
import type { GoalInstanceWithDetails } from '@/types/goal.types';

export const CelebrationGoalsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['goals', 'common']);
  const celebrationId = useMemo(() => Number.parseInt(id || '', 10), [id]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const [goalInstances, setGoalInstances] = useState<GoalInstanceWithDetails[]>([]);

  const { role } = useTeamMemberRole(celebrationId);
  const canEdit = canEditCelebration(role);

  useEffect(() => {
    if (!Number.isFinite(celebrationId) || celebrationId <= 0) {
      setError(t('goals:page.errors.invalidId'));
      setLoading(false);
      return;
    }

    const loadCelebration = async () => {
      try {
        setLoading(true);
        setError(null);
        const [celebrationData, goalsData] = await Promise.all([
          celebrationService.getCelebration(celebrationId),
          goalService.getInstancesByEntity('celebration', celebrationId),
        ]);
        setCelebration(celebrationData);
        setGoalInstances(goalsData);
      } catch (_err) {
        setError(t('goals:page.errors.failedLoad'));
      } finally {
        setLoading(false);
      }
    };

    void loadCelebration();
  }, [celebrationId]);

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="default"
        title={t('goals:page.loading.title')}
        description={t('goals:page.loading.description')}
      />
    );
  }

  if (error || !celebration) {
    return (
      <PageState
        kind="error"
        width="default"
        title={t('goals:page.error.title')}
        description={error || t('goals:page.error.notFound')}
      />
    );
  }

  const totalGoals = goalInstances.length;
  const completedGoals = goalInstances.filter((goal) => goal.status === 'completed').length;
  const inProgressGoals = totalGoals - completedGoals;
  const totalTasks = goalInstances.reduce((sum, goal) => sum + (goal.progress?.total_tasks || 0), 0);
  const completedTasks = goalInstances.reduce((sum, goal) => sum + (goal.progress?.completed_tasks || 0), 0);

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: 'Home', to: '/home' },
          { label: 'Celebrations', to: '/celebrations' },
          { label: celebration.title, to: `/celebrations/${celebration.id}` },
          { label: 'Goals' },
        ]}
      />

      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">{t('goals:page.title')}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{t('goals:page.subtitle')}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center h-8 rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-muted"
                onClick={() => navigate(`/celebrations/${celebration.id}`)}
              >
                {t('common:actions.back')}
              </button>
              <button
                type="button"
                className="inline-flex items-center h-8 rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-muted"
                onClick={() => navigate(`/celebrations/${celebration.id}/tasks`)}
              >
                {t('common:nav.tasks')}
              </button>
            </div>
          </div>
        }
        columns={4}
        items={[
          {
            key: 'total-goals',
            icon: Target,
            iconClassName: 'text-violet-600',
            hoverClassName: 'hover:bg-violet-50',
            label: t('goals:page.stats.totalGoals'),
            value: totalGoals,
          },
          {
            key: 'in-progress',
            icon: Clock3,
            iconClassName: 'text-amber-600',
            hoverClassName: 'hover:bg-amber-50',
            label: t('goals:page.stats.inProgress'),
            value: inProgressGoals,
          },
          {
            key: 'completed',
            icon: CheckCircle2,
            iconClassName: 'text-emerald-600',
            hoverClassName: 'hover:bg-emerald-50',
            label: t('goals:page.stats.completed'),
            value: completedGoals,
          },
          {
            key: 'task-progress',
            icon: ListTodo,
            iconClassName: 'text-blue-600',
            hoverClassName: 'hover:bg-blue-50',
            label: t('goals:page.stats.tasksDone'),
            value: `${completedTasks}/${totalTasks}`,
          },
        ]}
      />

      <GoalsCard entityType="celebration" entityId={celebration.id} canEdit={canEdit} />
    </PageContainer>
  );
};

export default CelebrationGoalsPage;